import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatApiErrorDetail } from "../lib/api";
import SectionHeader from "../components/SectionHeader";
import { Briefcase, MapPin, Clock, X } from "lucide-react";
import { toast } from "sonner";

export default function CareersPage() {
  const { t } = useTranslation();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get("/careers").then(({ data }) => { setJobs(data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  return (
    <div data-testid="careers-page" className="pb-24">
      <section className="border-b border-slate-800 py-20">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <SectionHeader kicker="JOIN THE TEAM" title={t("careers.title")} description={t("careers.subtitle")} testIdPrefix="careers" />
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="font-mono-spec text-xs text-amber-400 mb-6">{t("careers.open_positions").toUpperCase()}</div>
          {loading ? (
            <div className="text-slate-400">Loading…</div>
          ) : (
            <div className="border border-slate-800">
              {jobs.map((job) => (
                <div
                  key={job.id}
                  data-testid={`job-card-${job.id}`}
                  className="flex flex-col lg:flex-row lg:items-center justify-between p-6 border-b border-slate-800 last:border-b-0 bg-slate-950 hover:bg-slate-900 transition-colors"
                >
                  <div className="flex items-start gap-4">
                    <div className="mt-1 w-10 h-10 flex items-center justify-center border border-slate-800 bg-slate-900">
                      <Briefcase size={16} className="text-amber-400" />
                    </div>
                    <div>
                      <h3 className="font-display text-lg font-bold text-white">{job.title}</h3>
                      <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-xs text-slate-400 font-mono-spec">
                        <span className="inline-flex items-center gap-1"><MapPin size={12} /> {job.location}</span>
                        <span className="inline-flex items-center gap-1"><Clock size={12} /> {job.type}</span>
                        <span>{job.department}</span>
                      </div>
                      <p className="mt-3 text-sm text-slate-400 max-w-2xl">{job.description}</p>
                    </div>
                  </div>
                  <button
                    data-testid={`apply-btn-${job.id}`}
                    onClick={() => setSelected(job)}
                    className="mt-4 lg:mt-0 lg:ml-6 inline-flex items-center justify-center bg-amber-500 text-slate-950 font-bold px-5 py-2.5 hover:bg-amber-400 transition-colors rounded-sm text-sm self-start"
                  >
                    {t("careers.apply")}
                  </button>
                </div>
              ))}
              {jobs.length === 0 && <div className="p-8 text-slate-400">No open positions right now. Please check back soon.</div>}
            </div>
          )}
        </div>
      </section>

      {selected && <ApplyModal job={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

function ApplyModal({ job, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", cover_letter: "", resume_url: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/careers/apply", { ...form, position_id: job.id });
      toast.success("Application submitted. We will be in touch.");
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to submit.");
    } finally { setSubmitting(false); }
  };

  return (
    <div data-testid="apply-modal" className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-slate-950 border border-slate-800 max-w-lg w-full">
        <div className="flex items-center justify-between p-5 border-b border-slate-800">
          <div>
            <div className="font-mono-spec text-xs text-amber-400">APPLY</div>
            <h3 className="font-display font-bold text-white text-lg mt-1">{job.title}</h3>
          </div>
          <button data-testid="apply-modal-close" onClick={onClose} className="text-slate-400 hover:text-white p-2"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <Input label="Full name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required testid="apply-name" />
          <Input label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required testid="apply-email" />
          <Input label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="apply-phone" />
          <Input label="Resume URL" value={form.resume_url} onChange={(v) => setForm({ ...form, resume_url: v })} testid="apply-resume" placeholder="Link to Drive / Dropbox" />
          <label className="block">
            <span className="text-xs font-mono-spec text-slate-400">Cover letter</span>
            <textarea
              data-testid="apply-cover"
              className="mt-1 w-full bg-slate-900 border border-slate-800 focus:border-amber-500 focus:ring-0 outline-none text-white p-3 rounded-sm text-sm"
              rows={4}
              value={form.cover_letter}
              onChange={(e) => setForm({ ...form, cover_letter: e.target.value })}
            />
          </label>
          <button
            type="submit"
            data-testid="apply-submit"
            disabled={submitting}
            className="w-full bg-amber-500 text-slate-950 font-bold px-5 py-3 hover:bg-amber-400 transition-colors rounded-sm disabled:opacity-60"
          >
            {submitting ? "Submitting…" : "Submit application"}
          </button>
        </form>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, required, type = "text", testid, placeholder }) {
  return (
    <label className="block">
      <span className="text-xs font-mono-spec text-slate-400">{label}{required && <span className="text-amber-400"> *</span>}</span>
      <input
        data-testid={testid}
        type={type}
        required={required}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full bg-slate-900 border border-slate-800 focus:border-amber-500 focus:ring-0 outline-none text-white p-3 rounded-sm text-sm"
      />
    </label>
  );
}
